/**
 * Client-side image downsize before upload.
 *
 * WHY: modern phones ship 12–48 MP sensors. A single fuel-receipt photo
 * off a Galaxy S24 hits ~8 MB. Multiply by 30 dockets a week and we're
 * chewing through mobile data + tradie-patience. Downsizing on the
 * device also gets us under the ingress size limit on cheap network
 * paths (bad reception on-site is the norm, not the exception).
 *
 * Contract:
 *   - Input: any `File | Blob` whose `type` starts with `image/`.
 *   - Output: a NEW `File` with the same base name, `.jpg` extension,
 *     `image/jpeg` MIME type, and pixel dimensions capped at
 *     `maxEdge` (longest side) at the requested `quality`.
 *   - Non-image inputs (`application/pdf`, `text/csv`, etc.) are
 *     returned untouched — this helper only touches raster images.
 *   - Any decode / draw failure falls back to the ORIGINAL file, so a
 *     corrupt HEIC never blocks the upload flow. The tradie's phone
 *     time is more valuable than a 5 MB byte-saving.
 *
 * NOT here:
 *   - No EXIF stripping beyond what canvas re-encoding does naturally
 *     (which is: everything). If we later need to keep GPS tags for a
 *     "job site" feature, revisit.
 *   - No progressive JPEG toggle — Chrome's canvas encoder doesn't
 *     expose it. The default baseline is fine for receipts.
 */

const DEFAULT_MAX_EDGE = 1200;
const DEFAULT_QUALITY = 0.70;

/**
 * @param {File|Blob} file
 * @param {{ maxEdge?: number, quality?: number, mime?: string }} [opts]
 * @returns {Promise<File>}
 */
export async function resizeImage(file, opts = {}) {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts.quality ?? DEFAULT_QUALITY;
  const outMime = opts.mime ?? "image/jpeg";

  // Guard: not an image → pass through untouched.
  if (!file || typeof file.type !== "string" || !file.type.startsWith("image/")) {
    return file;
  }

  // Guard: some Android browsers ship HEIC via <input capture>. `createImageBitmap`
  // rejects those. If decode fails at any point we return the original file
  // and let the server / PDF layer downsize as it can.
  let bitmap;
  try {
    // `createImageBitmap` respects EXIF orientation on Safari 15+, Chrome 79+,
    // Firefox 77+ — the ONLY viable path if we want portrait-taken receipts
    // to land upright without hand-parsing EXIF ourselves.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file;
  }

  const { width: w, height: h } = bitmap;
  // No-op if the file is already small enough — save the CPU cycle and
  // preserve the source encoding (which may be more efficient than a
  // reencode-to-JPEG for a small PNG screenshot etc.).
  if (Math.max(w, h) <= maxEdge) {
    bitmap.close?.();
    return file;
  }

  const scale = maxEdge / Math.max(w, h);
  const targetW = Math.round(w * scale);
  const targetH = Math.round(h * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) {
    bitmap.close?.();
    return file;
  }
  // Slight quality bump for downscale — the default `medium` is soft on
  // small text (receipts have plenty of that).
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#ffffff"; // JPEG has no alpha; letterbox PNGs to white
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close?.();

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, outMime, quality)
  );
  if (!blob) return file;

  // Preserve the original filename base for accountant-friendly headings,
  // but swap the extension so servers that sniff on suffix know it's JPEG now.
  const base = (file.name || "photo").replace(/\.[^.]+$/, "");
  const filename = `${base}.jpg`;
  return new File([blob], filename, {
    type: outMime,
    lastModified: Date.now(),
  });
}

/**
 * Batch helper — resizes every image in `files`, returns an array
 * preserving order. Non-image files pass through untouched.
 *
 * @param {Array<File|Blob>} files
 * @param {Parameters<typeof resizeImage>[1]} [opts]
 */
export async function resizeImages(files, opts) {
  return Promise.all(Array.from(files || []).map((f) => resizeImage(f, opts)));
}
